import PrimaryButton from '@/components/Buttons/PrimaryButton';
import SecondaryButton from '@/components/Buttons/SecondaryButton';
import useInertiaData from '@/hooks/useInertiaData';
import useRoutes from '@/hooks/useRoutes';
import { router } from '@inertiajs/react';
import React from 'react';
import { useTranslation } from 'react-i18next';

const DISCORD_GUILD_IDS: Record<string, string> = {
    cblocks: '1005054815207968788',
    clanguages: '1336357723649998960',
};

const DiscordConnection: React.FC = () => {
    const { t } = useTranslation();
    const { user, discord_in_current_guild, discord_guild_id } =
        useInertiaData();
    const { route } = useRoutes();

    if (!user || user.discord_connect_disabled) {
        return null;
    }

    const theme = user.current_academy?.theme;
    const resolvedGuildId =
        discord_guild_id || (theme ? DISCORD_GUILD_IDS[theme] : null);

    const isDiscordConnected = Boolean(discord_in_current_guild);
    const discordStatusLabel = isDiscordConnected
        ? t('myAccount.discordConnectedAs', {
              username: user.discord_username || user.discord_user_id,
          })
        : t('myAccount.discordDisconnected');

    const handleConnect = () => {
        try {
            const url = route('discord-connect').toString();
            window.location.href = url;
        } catch (error) {
            console.error('Discord connect route is not available', error);
        }
    };

    const handleDisconnect = () => {
        const confirmed = window.confirm(
            t('myAccount.discordDisconnectConfirm') ||
                'Are you sure you want to disconnect your Discord account? You will be removed from the Discord server.'
        );

        if (confirmed) {
            try {
                router.post(route('discord-disconnect').toString());
            } catch (error) {
                console.error(
                    'Discord disconnect route is not available',
                    error
                );
            }
        }
    };

    return (
        <div className="border-t border-white/40 px-4 py-6 sm:px-8 dark:border-white/10">
            <h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
                {t('myAccount.discordConnection')}
            </h3>

            <p className="mb-6 text-sm text-gray-600 dark:text-neutral-400">
                {t('myAccount.discordConnectionDesc')}
            </p>

            <div className="flex flex-col gap-4 rounded-lg border border-white/40 bg-white/40 p-4 sm:flex-row sm:items-center sm:justify-between dark:border-white/10 dark:bg-white/5">
                <div>
                    <dt className="mb-1 text-sm font-medium text-gray-500 dark:text-neutral-400">
                        {t('home.status')}
                    </dt>
                    <dd className="text-base">
                        <span
                            className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${isDiscordConnected ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'}`}
                        >
                            {discordStatusLabel}
                        </span>
                    </dd>
                </div>
                {isDiscordConnected ? (
                    <div className="flex flex-col gap-2 sm:flex-row">
                        {resolvedGuildId && (
                            <a
                                href={`https://discord.com/channels/${resolvedGuildId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#57c464] px-5 py-2 text-sm font-bold tracking-wide text-white uppercase transition hover:bg-[#4ab357]"
                            >
                                {t('myAccount.discordGoToServer')}
                                <svg
                                    viewBox="0 0 24 24"
                                    className="h-5 w-5 fill-current"
                                    aria-hidden="true"
                                >
                                    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057c.002.022.015.043.03.056a19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
                                </svg>
                            </a>
                        )}
                        <SecondaryButton
                            type="button"
                            onClick={handleConnect}
                            size="md"
                        >
                            {t('myAccount.discordReconnectButton')}
                        </SecondaryButton>
                        <SecondaryButton
                            type="button"
                            onClick={handleDisconnect}
                            size="md"
                            className="text-red-600 hover:bg-red-50 hover:text-red-700"
                        >
                            {t('myAccount.discordDisconnectButton') ||
                                'Disconnect'}
                        </SecondaryButton>
                    </div>
                ) : (
                    <PrimaryButton
                        type="button"
                        onClick={handleConnect}
                        size="md"
                    >
                        {t('myAccount.discordConnectButton')}
                    </PrimaryButton>
                )}
            </div>
        </div>
    );
};

export default DiscordConnection;
